// FA204/FA207 / frank trap — `pad2` (a sibling export) and `_tzParts` (a
// module-scope helper backed by a module-scope memoization `Map`) were both
// read bare from other exports' own bodies: unresolvable once this file's
// exports are individually frank.toJSON'd for platform delivery
// (project_frank_drops_module_local_helpers; readerVoice.js precedent). Both
// are now self-contained locals per consumer. The per-timezone
// Intl.DateTimeFormat memoization is DROPPED rather than re-homed (there's no
// el/root argument anywhere in this file to hoist a shared cache onto, and a
// stale/undefined cache here would throw — `_tzPartsCache.get` on
// `undefined` — not just degrade quietly, unlike the documented
// _wsHandleResolver-style exceptions elsewhere in this sweep) — each call
// constructs its own formatter; correctness is unaffected, only the
// cross-call memoization is lost.
export const pad2 = (n) => String(n).padStart(2, '0')

// Local date/time formatters. Without `tz`, fall back to the browser's
// system timezone via the Date getters. When `tz` is supplied (e.g.
// 'Asia/Tbilisi') we use `Intl.DateTimeFormat` to get the right zone
// regardless of the system clock — fixes the WORKSPACE-CAL-TZ-DISPLAY
// case where a user in UTC+4 saw event times in UTC because the browser
// TZ didn't match their profile timezone.
export const localDate = (d, tz) => {
  const pad = (n) => String(n).padStart(2, '0')
  if (!tz) return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  const tzParts = (date, timeZone) => {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
    const parts = {}
    for (const p of fmt.formatToParts(date)) {
      if (p.type !== 'literal') parts[p.type] = p.value
    }
    // Intl returns '24' for midnight in some locales — normalize to '00'.
    if (parts.hour === '24') parts.hour = '00'
    return parts
  }
  const p = tzParts(d, tz)
  return `${p.year}-${p.month}-${p.day}`
}

export const localTime = (d, tz) => {
  const pad = (n) => String(n).padStart(2, '0')
  if (!tz) return `${pad(d.getHours())}:${pad(d.getMinutes())}`
  const tzParts = (date, timeZone) => {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
    const parts = {}
    for (const p of fmt.formatToParts(date)) {
      if (p.type !== 'literal') parts[p.type] = p.value
    }
    if (parts.hour === '24') parts.hour = '00'
    return parts
  }
  const p = tzParts(d, tz)
  return `${p.hour}:${p.minute}`
}

export const todayISO = () => {
  const local = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return local(new Date())
}

// `function`, not an arrow: `el.call('dayLabel', iso)` binds the element as
// `this`, and the label has to read in the SESSION language. "Today",
// "Yesterday" and the weekday / month names were English on a Georgian page
// (chat day dividers, live walk 2026-09-21). A caller with no element (a bare
// import, a test) keeps the old browser-locale English. Same binding contract
// as functions/pageTitle.js.
export function dayLabel(iso) {
  const el = this && typeof this.call === 'function' ? this : null
  const word = (key, fallback) => {
    const text = el ? el.call('polyglot', key) : null
    return text && text !== key ? text : fallback
  }
  const local = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  if (local(d) === local(today)) return word('common.time.today', 'Today')
  if (local(d) === local(yesterday)) return word('common.time.yesterday', 'Yesterday')
  const opts = { weekday: 'long', month: 'short', day: 'numeric' }
  // Only a non-empty STRING counts: a registry without localizedDate (a
  // standalone surface, a test stub that echoes its argument) keeps the old
  // browser-locale output rather than painting nothing, or a Date.
  const out = el ? el.call('localizedDate', d, opts) : ''
  return typeof out === 'string' && out ? out : d.toLocaleDateString(undefined, opts)
}

export function clockTime(ts) {
  const el = this && typeof this.call === 'function' ? this : null
  const d = new Date(ts)
  const opts = { hour: '2-digit', minute: '2-digit' }
  const out = el ? el.call('localizedDate', d, opts) : ''
  return typeof out === 'string' && out ? out : d.toLocaleTimeString(undefined, opts)
}

// Canonical ABSOLUTE-date formatter for list/card captions app-wide
// (el.call('shortDate', value)) — the shared counterpart to `timeAgo`
// (relative) and `dayLabel` (Today/Yesterday/weekday). DATE-FMT-1: the Home
// Notes widget rendered a clean date on one load and a raw
// `2026-08-14T00:03:57.756Z` ISO string on the next — root cause was TWO
// home-dashboard fetch declarations sharing the exact same fetch-plugin
// cache key/state slot (`docs:select:` → `root.documents`) with DIFFERENT
// per-callsite transforms: one pre-formatted `updatedAt` at fetch time
// (`.slice(0, 10)`), the other (matching the canonical `documentsFetch` used
// by `/notes`) left it as the raw server ISO string. Whichever transform's
// async response landed LAST silently won the shared state slot — an
// intermittent race, not a missing formatter. Fix: never format inside a
// fetch `transform` (that's exactly the per-callsite drift that caused the
// bug) — always format ONCE at the render boundary via this function, so a
// raw ISO value can reach the DOM only if this function itself is skipped.
// Any unparsable/missing input degrades to '' rather than leaking raw text.
export function shortDate(value) {
  const el = this && typeof this.call === 'function' ? this : null
  if (!value) return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const opts = { month: 'short', day: 'numeric' }
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric'
  const out = el ? el.call('localizedDate', d, opts) : ''
  return typeof out === 'string' && out ? out : d.toLocaleDateString(undefined, opts)
}
